import type { EffectParticle } from '../TextParticles.types';
import { lerp } from './math-utils';

export type CanvasDimensions = ReturnType<typeof getCanvasDimensions>;

export function getCanvasDimensions(
  textBoundingBox: Pick<DOMRect, 'width' | 'height'> | undefined,
  spread: number = 1,
) {
  const devicePixelRatio =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  const boundingBoxWidth = textBoundingBox?.width || 0;
  const boundingBoxHeight = textBoundingBox?.height || 0;

  if (boundingBoxWidth === 0 && boundingBoxHeight === 0) {
    return undefined;
  }

  const displayWidth = boundingBoxWidth * spread;
  const displayHeight = boundingBoxHeight * spread;

  return {
    displayWidth,
    displayHeight,

    offscreenWidth: displayWidth * devicePixelRatio,
    offscreenHeight: displayHeight * devicePixelRatio,
    offscreenScale: devicePixelRatio,
  };
}

const drawCircle = ({
  context,
  x,
  y,
  radius,
  color,
}: {
  context: OffscreenCanvasRenderingContext2D;
  x: number;
  y: number;
  radius: number;
  color: string;
}) => {
  context.beginPath();
  context.arc(x, y, radius, 0, 2 * Math.PI);
  context.fillStyle = color;
  context.fill();
};

export const renderEffectParticles = (
  effectParticles: Array<EffectParticle>,
  context: OffscreenCanvasRenderingContext2D,
) => {
  effectParticles.forEach(effectParticle => {
    const sizeProgress = lerp(
      effectParticle.particleStartSize,
      effectParticle.particleEndSize,
      effectParticle.progress,
    );
    const updatedRadius = effectParticle.radius * sizeProgress;

    const opacityProgress = lerp(
      effectParticle.particleStartOpacity,
      effectParticle.particleEndOpacity,
      effectParticle.progress,
    );
    const opacity = opacityProgress / 100;

    const previousGlobalAlpha = context.globalAlpha;

    context.globalAlpha = opacity;

    drawCircle({
      context,
      x: effectParticle.x,
      y: effectParticle.y,
      radius: updatedRadius,
      color: getColorFromProgress(
        effectParticle.colorProgression,
        effectParticle.progress,
      ),
    });

    context.globalAlpha = previousGlobalAlpha;
  });
};

export const getColorFromProgress = (
  colors: Array<string>,
  progress: number,
): string => {
  if (!colors?.length) {
    return 'rgb(0, 0, 0)';
  }

  if (colors.length === 1) {
    return colors[0];
  }

  if (progress === 1) {
    return colors[colors.length - 1];
  }

  const clampedProgress = Math.max(0, Math.min(1, progress));

  const segment = clampedProgress * (colors.length - 1);
  const segmentIndex = Math.floor(segment);

  const segmentProgress = segment - segmentIndex;

  const color1 = parseColor(colors[segmentIndex]);
  const color2 = parseColor(colors[segmentIndex + 1]);

  const r = Math.round(lerp(color1.r, color2.r, segmentProgress));
  const g = Math.round(lerp(color1.g, color2.g, segmentProgress));
  const b = Math.round(lerp(color1.b, color2.b, segmentProgress));

  return `rgb(${r}, ${g}, ${b})`;
};

const parseColor = (color: string): { r: number; g: number; b: number } => {
  const [r, g, b] = color
    .replace('rgb(', '')
    .replace(')', '')
    .split(',')
    .map(Number);
  return { r, g, b };
};
